// src/api/practice.ts
// THE TYPED SURFACE OF /v1/practice/**, transcribed from the routers — not guessed.
//
// Sources, all in dcs-sports-backend/sb on branch gate-a/remediation-26aug2026:
//   src/practice/routes/sessions.ts          session lifecycle + the offline sync batch
//   src/practice/routes/identity.ts          roster candidates, session QR, the human confirmation
//   src/practice/routes/uploads.ts           initiate / presign / status / complete / abort
//   src/practice/routes/academy-practice.ts  batch sessions, lanes, coach today, parent surfaces
//   src/practice/routes/passport-bridge.ts   practice → Passport evidence
//   src/practice/upload-service.ts           the result shapes and the failure reason enum
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE RULE THIS FILE ENFORCES BY SHAPE: NOTHING IN A BODY CARRIES IDENTITY
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Every router in that backend repeats the same sentence, because three body-trusted-identity
// defects were fixed there in one day: the SUBJECT is a path parameter and the ACTOR is the verified
// bearer principal. So in this file the athlete/academy/session id is always a positional ARGUMENT
// that goes into the path, and the body types below contain NO `athlete_id`, NO `user_id`, NO
// `coach_id`, NO `confirmed_by`. They are not omitted by oversight — a field that does not exist in
// the type cannot be sent by a careless screen.
//
// The single exception is `entries[].athlete_id` on the batch POST, and it is the same exception the
// backend documents: those are the SUBJECTS of a batch ("these twelve children"), never the
// principal, and academy-service re-gates each one individually. A forged id comes back in `refused`.
//
// AND: an athlete id is only ever passed here after it came out of a server response — the roster
// candidates list or `parent/children`. The brief's "never send an athlete id from a body the server
// did not give you" is upheld at the call sites in ui/screens, not by a comment here.

import { request, type ApiResult } from './client';
import { PRACTICE_NAMESPACE as NS } from '../config';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SESSIONS — src/practice/routes/sessions.ts
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** The 041 CHECK vocabulary. Sending anything else is a 400 from the database's own constraint. */
export const CAMERA_ROLES = ['BALL_VIEW', 'TECHNIQUE_VIEW', 'BATTER_VIEW', 'PRECISION'] as const;
export type CameraRole = (typeof CAMERA_ROLES)[number];

/**
 * Session modes — `SESSION_MODES` in the backend's `src/practice/session-service.ts`, verbatim.
 *
 * ── `NET_PRACTICE` WAS NEVER A MODE (fixed 26 Aug 2026) ──
 * Two screens shipped a free-text Mode box defaulting to the string `NET_PRACTICE`, which appears
 * nowhere in the backend, in migration 041, or in the roadmap. It reads like a mode, so nobody
 * questioned it. EVERY session creation failed with the server's own list quoted back —
 * "mode must be one of LIVE_BOWLER|BOWLING_MACHINE|SIDEARM_THROWDOWN|MANUAL_DRILL" — and because
 * uploads and deliveries both need a session id, the whole capture path was unreachable.
 *
 * The vocabulary is not decoration. The batting lane keys its entire confidence model on it: a
 * BOWLING_MACHINE preset is a RECORDED input the athlete faced, while a LIVE_BOWLER ball is an
 * ESTIMATE the model inferred. Those earn different measurement classes. A free-text box could
 * never carry that distinction, so this is a `select` over the real four and nothing else.
 */
export const SESSION_MODES = [
  'LIVE_BOWLER', 'BOWLING_MACHINE', 'SIDEARM_THROWDOWN', 'MANUAL_DRILL',
] as const;
export type SessionMode = (typeof SESSION_MODES)[number];

export interface CreateSessionBody {
  /** Generated on this device, persisted, NEVER regenerated on retry. See device/ids.ts. */
  client_device_id: string;
  /** Generated once per session attempt. THE idempotency key — same value on every replay. */
  client_session_uuid: string;
  mode: string;
  camera_role: CameraRole;
  athlete_focus?: string;
  environment?: string;
  started_at?: string;
  is_synthetic?: boolean;
  demo_label?: string;
  academy_id?: string;
  context_json?: Record<string, unknown>;
  device_json?: Record<string, unknown>;
}

export interface PracticeSession {
  id: string;
  athlete_id: string;
  client_session_uuid: string;
  client_device_id: string;
  mode: string;
  camera_role: string;
  started_at: string | null;
  completed_at: string | null;
  sync_state: string | null;
  lane?: string | null;
  /** The sentence the backend wrote for the athlete. Rendered verbatim where present. */
  retry_state?: string | null;
  [k: string]: unknown;
}

/**
 * POST /v1/practice/athletes/:athleteId/sessions
 *
 * 200 on the first call AND on every replay, deliberately — the backend refuses to let a phone tell
 * the difference. `replayed: true` means the row already existed and nothing was written.
 */
export function createSession(
  athleteId: string,
  body: CreateSessionBody,
): Promise<ApiResult<{ session: PracticeSession; replayed: boolean; subject_is_minor: boolean; role: string }>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/sessions`, { method: 'POST', body });
}

export interface SyncBatchResult {
  committed: PracticeSession[];
  already_committed_uuids: string[];
  in_batch_duplicate_uuids: string[];
  in_batch_duplicate_seq_ids: number[];
  /** The honest report that the device's queue lost something between two sequence numbers. */
  gaps: unknown[];
}

/** POST …/sessions/sync — the whole-queue form. `gaps` is surfaced, never smoothed over. */
export function syncSessions(
  athleteId: string,
  body: { client_device_id: string; sessions: Array<CreateSessionBody & { client_seq_id: number }> },
): Promise<ApiResult<SyncBatchResult>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/sessions/sync`, { method: 'POST', body });
}

export function listSessions(
  athleteId: string,
  filter: 'all' | 'open' | 'unsynced' = 'all',
): Promise<ApiResult<{ sessions: PracticeSession[]; filter: string }>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/sessions`, { query: { filter } });
}

/** GET …/sessions/resume — everything not both completed AND synced, so the app REJOINS. */
export function resumeSessions(
  athleteId: string,
  clientDeviceId: string,
): Promise<ApiResult<{ resumable: PracticeSession[] }>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/sessions/resume`, {
    query: { client_device_id: clientDeviceId },
  });
}

export function getSession(sessionId: string): Promise<ApiResult<{ session: PracticeSession; calibrations: unknown[] }>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}`);
}

/** POST …/sessions/:id/end — idempotent; a replay returns the FIRST completed_at, untouched. */
export function endSession(
  sessionId: string,
  body: { completed_at?: string; sync_state?: string } = {},
): Promise<ApiResult<{ session: PracticeSession; already_completed: boolean }>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/end`, { method: 'POST', body });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * IDENTITY — src/practice/routes/identity.ts
 *
 * NO FACE RECOGNITION EXISTS ON THIS SURFACE, and no client-side shortcut may simulate one. The
 * candidate list has no write path; assignment requires a separate call carrying a literal
 * `confirmed: true` that a HUMAN produced. This client renders that as an explicit confirm step
 * with the athlete named — never an auto-select, never a default selection.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface RosterCandidate {
  athlete_id: string;
  /** Always true from this endpoint. Rendered, not assumed. */
  requires_confirmation: boolean;
  display_name?: string | null;
  subject_is_minor?: boolean;
  [k: string]: unknown;
}

export interface CandidatesResult {
  academy_id: string;
  candidates: RosterCandidate[];
  /** Says plainly when a bib hint narrowed nothing. Rendered verbatim. */
  hint?: unknown;
}

/** GET …/identity/candidates?bib_hint= — the roster. It cannot assign; it has no write path. */
export function listRosterCandidates(academyId: string, bibHint?: string): Promise<ApiResult<CandidatesResult>> {
  return request(`${NS}/academies/${encodeURIComponent(academyId)}/identity/candidates`, {
    query: { bib_hint: bibHint ?? null },
  });
}

export interface SessionQr {
  token: string;
  session_id: string;
  athlete_id: string;
  device_id: string;
  expires_at: string;
  ttl_seconds: number;
}

/** POST …/identity/qr — signed EVIDENCE binding one device to one athlete for one session. */
export function mintSessionQr(
  academyId: string,
  body: { session_id: string; athlete_id: string; device_id: string; ttl_seconds?: number },
): Promise<ApiResult<SessionQr>> {
  return request(`${NS}/academies/${encodeURIComponent(academyId)}/identity/qr`, { method: 'POST', body });
}

export type AssignmentMethod = 'ROSTER_TAP' | 'SESSION_QR';

export interface AssignmentResult {
  session_id: string;
  athlete_id: string;
  academy_id: string;
  method: AssignmentMethod;
  confirmed_by: string;
  subject_is_minor: boolean;
  confirmed_at: string;
  evidence: unknown;
}

/**
 * POST …/identity/assignments — THE human confirmation.
 *
 * `confirmed` is typed `true`, not `boolean`, so this client cannot express the request the backend
 * 409s on. Presenting a QR token is not enough and holding the roster open is not enough: a person
 * affirms, and the server still re-checks that they own the academy and that the athlete holds an
 * ACTIVE enrollment in it.
 */
export function confirmAssignment(
  academyId: string,
  body: {
    session_id: string;
    athlete_id: string;
    method: AssignmentMethod;
    confirmed: true;
    qr_token?: string;
    device_id?: string;
    bib_hint?: string;
    note?: string;
  },
): Promise<ApiResult<AssignmentResult>> {
  return request(`${NS}/academies/${encodeURIComponent(academyId)}/identity/assignments`, { method: 'POST', body });
}

export function assignmentHistory(
  academyId: string,
  sessionId: string,
): Promise<ApiResult<{ session_id: string; entries: unknown[]; current_athlete_id: string | null; confirmed: boolean }>> {
  return request(
    `${NS}/academies/${encodeURIComponent(academyId)}/identity/sessions/${encodeURIComponent(sessionId)}/history`,
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * UPLOADS — src/practice/routes/uploads.ts
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface AcknowledgedPart { part_number: number; etag: string; size: number }

export interface InitiateResult {
  athlete_id: string;
  role: string;
  client_upload_uuid: string;
  key: string;
  /** Null ONLY when `already_complete` — there is nothing left to send. */
  upload_id: string | null;
  part_size: number;
  part_count: number;
  total_bytes: number;
  content_type: string;
  acknowledged_parts: AcknowledgedPart[];
  missing_part_numbers: number[];
  replayed: boolean;
  already_complete: boolean;
  replay_source: 'none' | 'in_flight_upload' | 'object_present' | 'sha256_clip';
  retry_state: 'NOT_STARTED' | 'RESUMABLE' | 'COMPLETE';
  storage_provider: string;
}

/**
 * POST …/uploads — initiate, OR REJOIN.
 *
 * Call it as often as you like; it never forks. The phone that died after part 2 of 3 calls this and
 * is told `missing_part_numbers: [3]`. That is the entire resume mechanism.
 */
export function initiateUpload(
  athleteId: string,
  body: { client_upload_uuid: string; total_bytes: number; content_type?: string; part_size?: number; sha256?: string },
): Promise<ApiResult<InitiateResult>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/uploads`, { method: 'POST', body });
}

export interface PartUrl { part_number: number; url: string; expires_in: number; expires_at: string }

/** POST …/uploads/:uuid/parts — signed PUT URLs, each with an explicit expiry. Ask again when stale. */
export function presignParts(
  athleteId: string,
  clientUploadUuid: string,
  body: { part_numbers: number[]; upload_id?: string | null; content_type?: string; part_size?: number },
): Promise<ApiResult<{ parts: PartUrl[]; upload_id: string; key: string; part_size: number; expires_in: number; expires_at: string }>> {
  return request(
    `${NS}/athletes/${encodeURIComponent(athleteId)}/uploads/${encodeURIComponent(clientUploadUuid)}/parts`,
    { method: 'POST', body },
  );
}

export interface UploadStatusResult {
  athlete_id: string;
  client_upload_uuid: string;
  key: string;
  upload_id: string | null;
  retry_state: 'NOT_STARTED' | 'RESUMABLE' | 'COMPLETE';
  already_complete: boolean;
  part_size: number;
  part_count: number | null;
  acknowledged_part_numbers: number[];
  missing_part_numbers: number[];
  bytes_acknowledged: number;
  bytes_total: number | null;
  bytes_remaining: number | null;
  /**
   * THE only number this device may free local storage against. It counts bytes R2 has DURABLY
   * acknowledged. The UI shows this and never a byte more — deleting on optimism loses a capture.
   */
  safe_to_delete_local_bytes: number;
  /** Written to be shown to the athlete verbatim. It is, in ui/screens/upload.ts. */
  retry_message: string;
}

export function uploadStatus(
  athleteId: string,
  clientUploadUuid: string,
  q: { total_bytes?: number; content_type?: string; part_size?: number } = {},
): Promise<ApiResult<UploadStatusResult>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/uploads/${encodeURIComponent(clientUploadUuid)}`, {
    query: { total_bytes: q.total_bytes ?? null, content_type: q.content_type ?? null, part_size: q.part_size ?? null },
  });
}

export interface CompleteResult {
  athlete_id: string;
  client_upload_uuid: string;
  key: string;
  object_url: string;
  media_id: string;
  clip: Record<string, unknown> | null;
  clip_reason: 'created' | 'existing' | 'no_session_id' | 'invalid_camera_role';
  /** TRUE when this complete had already happened. NOTHING was written a second time. */
  replayed: boolean;
  retry_state: string;
  purge_reachable: true;
}

/** POST …/uploads/:uuid/complete — replay-safe: same media_id, same clip, nothing written twice. */
export function completeUpload(
  athleteId: string,
  clientUploadUuid: string,
  body: {
    parts: Array<{ part_number: number; etag: string }>;
    upload_id?: string | null;
    content_type?: string;
    session_id?: string;
    camera_role?: CameraRole;
    total_bytes?: number;
    duration_ms?: number;
    recorded_at?: string;
    is_synthetic?: boolean;
  },
): Promise<ApiResult<CompleteResult>> {
  return request(
    `${NS}/athletes/${encodeURIComponent(athleteId)}/uploads/${encodeURIComponent(clientUploadUuid)}/complete`,
    { method: 'POST', body },
  );
}

/** POST …/uploads/:uuid/abort — releases parts R2 would otherwise bill for. Idempotent. */
export function abortUpload(
  athleteId: string,
  clientUploadUuid: string,
  body: { upload_id?: string | null; content_type?: string } = {},
): Promise<ApiResult<{ aborted: boolean; upload_state: string; note: string }>> {
  return request(
    `${NS}/athletes/${encodeURIComponent(athleteId)}/uploads/${encodeURIComponent(clientUploadUuid)}/abort`,
    { method: 'POST', body },
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * COACH + PARENT — src/practice/routes/academy-practice.ts
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface CoachAthleteRow {
  athlete_id: string;
  subject_is_minor: boolean;
  sessions_today: Array<{ session_id: string; lane: string | null; started_at: string; completed: boolean; identity_confirmed: boolean }>;
  pending_reviews: number;
  workload_state: string;
  workload_flagged: boolean;
  /** The workload guardian's own closed set of sentences. Rendered unedited. */
  workload_messages: string[];
  needs_identity_confirmation: boolean;
}

export interface AcademyTodayView {
  academy_id: string;
  generated_at: string;
  day_start: string;
  athlete_count: number;
  athletes: CoachAthleteRow[];
  totals: { in_session_now: number; pending_reviews: number; workload_flagged: number; unconfirmed_identity: number };
  /**
   * Enumerated but NOT authorized. When this exceeds `athlete_count`, someone on the roster is not
   * someone this caller may read — the backend surfaces the difference on purpose and so does the
   * coach screen, rather than quietly absorbing it.
   */
  roster_candidates_seen: number;
  note: string;
}

export function academyToday(academyId: string): Promise<ApiResult<AcademyTodayView>> {
  return request(`${NS}/academies/${encodeURIComponent(academyId)}/today`);
}

export interface ParentChild {
  athlete_id: string;
  viewer_role: 'guardian' | 'owner';
  subject_is_minor: boolean;
}

/** GET /v1/practice/parent/children — a `legacy_unverified` link enumerates and is DROPPED. */
export function parentChildren(): Promise<ApiResult<{ children: ParentChild[]; note: string }>> {
  return request(`${NS}/parent/children`);
}

export type ParentProgressState = string;

export interface ParentProgressItem {
  /** The human label. Never a metric_id, never a jargon code. */
  what: string;
  state: ParentProgressState;
  /** ONE sentence. The only place a measured number may appear, and only under MEANINGFUL_CHANGE. */
  sentence: string;
  /** Null in every state but MEANINGFUL_CHANGE. */
  change_shown: string | null;
  reviewed_by_a_coach: boolean;
}

export interface ParentReport {
  athlete_id: string;
  viewer_role: 'guardian' | 'owner';
  generated_at: string;
  window: { from: string; to: string; days: number };
  participation: { sessions_attended: number; balls_recorded: number; last_session_at: string | null; days_practised: number };
  current_focus: { focus: string[]; note: string };
  progress: { scope: string; panel_source: string; items: ParentProgressItem[]; note: string };
  standard_test_trend: unknown;
  workload_and_rest: { state: string; guidance: string[]; scope: string };
  coach_comments: Array<{ comment: string; at: string; drill: { title: string; when_not_to_do_it: string } | null }>;
  approved_clips: ApprovedClip[];
  /** The backend's own list of what this page withholds. Rendered as-is; it is not decoration. */
  what_this_page_does_not_show: string[];
}

/** GET /v1/practice/parent/athletes/:id/report — a COACH asking for this is 403'd with a pointer. */
export function parentReport(athleteId: string): Promise<ApiResult<ParentReport>> {
  return request(`${NS}/parent/athletes/${encodeURIComponent(athleteId)}/report`);
}

export interface ApprovedClip {
  clip_id: string;
  session_id: string;
  camera_role: string;
  recorded_at: string | null;
  duration_ms: number | null;
  approved_at: string | null;
  approved_by: string | null;
  note: string | null;
  /** Deliberately NOT a url. The address is resolved one clip at a time, at the moment of playback. */
  playback_path: string;
}

export function parentClips(athleteId: string): Promise<ApiResult<{ clips: ApprovedClip[] } | ApprovedClip[]>> {
  return request(`${NS}/parent/athletes/${encodeURIComponent(athleteId)}/clips`);
}

/**
 * GET …/clips/:clipId/playback — THE one place a media URL leaves the parent surface.
 *
 * Called ONLY on an explicit tap, never eagerly on list render: the backend retakes the whole
 * decision (gate, latest ledger fact, ownership, retention) at this moment, and a withdrawal takes
 * effect on the very next call because there is no durable link to invalidate. Prefetching would
 * turn that live check into a stale one.
 */
export function parentClipPlayback(athleteId: string, clipId: string): Promise<ApiResult<Record<string, unknown>>> {
  return request(
    `${NS}/parent/athletes/${encodeURIComponent(athleteId)}/clips/${encodeURIComponent(clipId)}/playback`,
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * PASSPORT BRIDGE — src/practice/routes/passport-bridge.ts
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface PassportPracticeEvent {
  session_id?: string;
  at?: string | null;
  /** [] on every event today, with `not_available` saying why. Both are rendered. */
  shareable_clips?: unknown[];
  not_available?: unknown;
  [k: string]: unknown;
}

/** GET …/passport-events — a guardian gets `read_only: true`. The ROLE selects the surface, not us. */
export function passportEvents(
  athleteId: string,
  limit?: number,
): Promise<ApiResult<{ role: string; read_only: boolean; events: PassportPracticeEvent[] }>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/passport-events`, { query: { limit: limit ?? null } });
}

export function passportTimeline(athleteId: string): Promise<ApiResult<{ athlete_id: string; timeline: unknown[] }>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/passport-timeline`);
}

/**
 * NOT CALLED BY ANY SCREEN IN THIS BUILD, and that is deliberate.
 *
 * `?surface=` does not exist on the backend precisely so an athlete screen can never widen into a
 * scout screen. Exposing the outward surface in a client whose scope is "athlete, coach, parent"
 * would put the two one code-review-miss apart. It is declared here so the shape is on record, and
 * left uncalled.
 */
export function scoutEvidence(athleteId: string): Promise<ApiResult<Record<string, unknown>>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/scout-evidence`);
}
